// Wikidata place types with their QIDs and descriptions
class PlaceType {
    constructor(id, wikidataId, name, description, category = 'settlement', icon = '📍') {
        this.id = id;
        this.wikidataId = wikidataId; // Wikidata Q-ID
        this.name = name;
        this.description = description;
        this.category = category; // for grouping: settlement, administrative, natural, etc.
        this.icon = icon; // emoji icon for visual representation
    }
}

// Comprehensive place types organized by category
export const PLACE_TYPES = [
    // === SETTLEMENTS & URBAN AREAS ===
    new PlaceType('city', 'Q515', 'City', 'A large human settlement', 'settlement', '🏙️'),
    new PlaceType('bigcity', 'Q1549591', 'Big City', 'A very large city', 'settlement', '🌆'),
    new PlaceType('town', 'Q3957', 'Town', 'A human settlement larger than a village', 'settlement', '🏘️'),
    new PlaceType('smalltown', 'Q3558970', 'Small Town', 'A small urban settlement', 'settlement', '🏘️'),
    new PlaceType('village', 'Q532', 'Village', 'A small human settlement', 'settlement', '🏡'),
    new PlaceType('hamlet', 'Q5084', 'Hamlet', 'A very small rural settlement', 'settlement', '🏠'),
    new PlaceType('neighborhood', 'Q123705', 'Neighborhood', 'A district within a city', 'settlement', '🏘️'),
    new PlaceType('suburb', 'Q188509', 'Suburb', 'An outlying residential area', 'settlement', '🏡'),
    new PlaceType('resort', 'Q875157', 'Resort Town', 'A town for tourism/recreation', 'settlement', '🏖️'),

    // === ADMINISTRATIVE DIVISIONS ===
    new PlaceType('municipality', 'Q15284', 'Municipality', 'An administrative division', 'administrative', '🏛️'),
    new PlaceType('commune', 'Q3266850', 'Commune', 'A type of municipality', 'administrative', '🏛️'),
    new PlaceType('district', 'Q149621', 'District', 'An administrative division', 'administrative', '📋'),
    new PlaceType('county', 'Q28575', 'County', 'A territorial division', 'administrative', '📍'),
    new PlaceType('province', 'Q34876', 'Province', 'A territorial division', 'administrative', '🗺️'),
    new PlaceType('region', 'Q82794', 'Region', 'A large geographical area', 'administrative', '🌍'),

    // === NATURAL WATER FEATURES ===
    new PlaceType('lake', 'Q23397', 'Lake', 'A body of water surrounded by land', 'water', '🏞️'),
    new PlaceType('river', 'Q4022', 'River', 'A flowing watercourse', 'water', '🏞️'),
    new PlaceType('stream', 'Q47521', 'Stream', 'A small flowing watercourse', 'water', '💧'),
    new PlaceType('pond', 'Q12719646', 'Pond', 'A small body of standing water', 'water', '💧'),
    new PlaceType('reservoir', 'Q131681', 'Reservoir', 'An artificial lake for water storage', 'water', '🏞️'),
    new PlaceType('waterfall', 'Q34038', 'Waterfall', 'A place where water flows over a rock face', 'water', '💧'),
    new PlaceType('spring', 'Q124714', 'Spring', 'A natural source of water', 'water', '💧'),
    new PlaceType('bay', 'Q39594', 'Bay', 'A body of water connected to an ocean', 'water', '🌊'),
    new PlaceType('gulf', 'Q44783', 'Gulf', 'A large bay', 'water', '🌊'),
    new PlaceType('strait', 'Q8473', 'Strait', 'A narrow passage of water', 'water', '🌊'),

    // === MOUNTAINS & ELEVATED TERRAIN ===
    new PlaceType('mountain', 'Q8502', 'Mountain', 'A large elevated landform', 'terrain', '⛰️'),
    new PlaceType('mountain_range', 'Q46831', 'Mountain Range', 'A series of connected mountains', 'terrain', '🏔️'),
    new PlaceType('peak', 'Q207326', 'Peak', 'The top of a mountain', 'terrain', '🗻'),
    new PlaceType('hill', 'Q54050', 'Hill', 'A raised area of land', 'terrain', '⛰️'),
    new PlaceType('plateau', 'Q75520', 'Plateau', 'A flat elevated landform', 'terrain', '🏔️'),
    new PlaceType('ridge', 'Q1244695', 'Ridge', 'A long narrow elevated landform', 'terrain', '⛰️'),
    new PlaceType('valley', 'Q39816', 'Valley', 'A low area between hills or mountains', 'terrain', '🏞️'),
    new PlaceType('canyon', 'Q6746', 'Canyon', 'A deep gorge with steep sides', 'terrain', '🏔️'),
    new PlaceType('gorge', 'Q156537', 'Gorge', 'A narrow valley with steep sides', 'terrain', '🏔️'),
    new PlaceType('pass', 'Q133056', 'Mountain Pass', 'A route through mountains', 'terrain', '🚶'),

    // === FORESTS & VEGETATION ===
    new PlaceType('forest', 'Q4421', 'Forest', 'A large area covered with trees', 'vegetation', '🌲'),
    new PlaceType('wood', 'Q4421', 'Wood', 'A small forest', 'vegetation', '🌳'),
    new PlaceType('grove', 'Q2958444', 'Grove', 'A small group of trees', 'vegetation', '🌳'),
    new PlaceType('meadow', 'Q1006733', 'Meadow', 'A grassland habitat', 'vegetation', '🌾'),
    new PlaceType('prairie', 'Q2735', 'Prairie', 'A grassland ecosystem', 'vegetation', '🌾'),
    new PlaceType('marsh', 'Q30198', 'Marsh', 'A wetland with herbaceous plants', 'vegetation', '🌿'),
    new PlaceType('swamp', 'Q166735', 'Swamp', 'A wetland with woody plants', 'vegetation', '🌿'),

    // === COASTAL & GEOGRAPHIC FEATURES ===
    new PlaceType('cape', 'Q3770', 'Cape', 'A headland extending into water', 'coastal', '🏖️'),
    new PlaceType('peninsula', 'Q34763', 'Peninsula', 'Land surrounded by water on three sides', 'coastal', '🌊'),
    new PlaceType('island', 'Q23442', 'Island', 'Land surrounded by water', 'coastal', '🏝️'),
    new PlaceType('archipelago', 'Q33837', 'Archipelago', 'A group of islands', 'coastal', '🏝️'),
    new PlaceType('beach', 'Q40080', 'Beach', 'A shoreline with sand or pebbles', 'coastal', '🏖️'),
    new PlaceType('cliff', 'Q107425', 'Cliff', 'A steep rock face', 'coastal', '🏔️'),
    new PlaceType('dune', 'Q83261', 'Sand Dune', 'A hill of wind-blown sand', 'coastal', '🏜️'),

    // === DESERTS & ARID REGIONS ===
    new PlaceType('desert', 'Q8514', 'Desert', 'A dry, barren area of land', 'arid', '🏜️'),
    new PlaceType('oasis', 'Q43742', 'Oasis', 'A fertile area in a desert', 'arid', '🌴'),

    // === GLACIAL & POLAR FEATURES ===
    new PlaceType('glacier', 'Q35666', 'Glacier', 'A persistent body of dense ice', 'glacial', '🧊'),
    new PlaceType('icefield', 'Q1327063', 'Ice Field', 'A large area of ice', 'glacial', '❄️'),

    // === CULTURAL & HISTORICAL SITES ===
    new PlaceType('castle', 'Q23413', 'Castle', 'A fortified residence', 'cultural', '🏰'),
    new PlaceType('ruins', 'Q744099', 'Ruins', 'Remains of a structure', 'cultural', '🏛️'),
    new PlaceType('monument', 'Q4989906', 'Monument', 'A commemorative structure', 'cultural', '🗿'),
    new PlaceType('archaeological_site', 'Q839954', 'Archaeological Site', 'A place of historical significance', 'cultural', '🏺'),

    // === TRANSPORTATION & INFRASTRUCTURE ===
    new PlaceType('bridge', 'Q12280', 'Bridge', 'A structure spanning a physical obstacle', 'infrastructure', '🌉'),
    new PlaceType('tunnel', 'Q44377', 'Tunnel', 'An underground passage', 'infrastructure', '🕳️'),
    new PlaceType('port', 'Q44782', 'Port', 'A harbor for ships', 'infrastructure', '⚓'),
    new PlaceType('airport', 'Q1248784', 'Airport', 'A facility for aircraft', 'infrastructure', '✈️'),
];

// Group place types by category with metadata
export const PLACE_TYPE_CATEGORIES = {
    settlement: {
        name: 'Settlements & Urban Areas',
        icon: '🏙️',
        description: 'Cities, towns, villages, and human settlements',
        types: PLACE_TYPES.filter(type => type.category === 'settlement')
    },
    administrative: {
        name: 'Administrative Divisions',
        icon: '🏛️',
        description: 'Political and administrative boundaries',
        types: PLACE_TYPES.filter(type => type.category === 'administrative')
    },
    water: {
        name: 'Water Features',
        icon: '🌊',
        description: 'Lakes, rivers, seas, and other water bodies',
        types: PLACE_TYPES.filter(type => type.category === 'water')
    },
    terrain: {
        name: 'Mountains & Terrain',
        icon: '⛰️',
        description: 'Mountains, hills, valleys, and land formations',
        types: PLACE_TYPES.filter(type => type.category === 'terrain')
    },
    vegetation: {
        name: 'Forests & Vegetation',
        icon: '🌲',
        description: 'Forests, woods, meadows, and plant ecosystems',
        types: PLACE_TYPES.filter(type => type.category === 'vegetation')
    },
    coastal: {
        name: 'Coastal Features',
        icon: '🏖️',
        description: 'Islands, capes, beaches, and coastal formations',
        types: PLACE_TYPES.filter(type => type.category === 'coastal')
    },
    arid: {
        name: 'Deserts & Arid Regions',
        icon: '🏜️',
        description: 'Deserts, oases, and dry landscapes',
        types: PLACE_TYPES.filter(type => type.category === 'arid')
    },
    glacial: {
        name: 'Glacial & Polar',
        icon: '🧊',
        description: 'Glaciers, ice fields, and frozen landscapes',
        types: PLACE_TYPES.filter(type => type.category === 'glacial')
    },
    cultural: {
        name: 'Cultural & Historical',
        icon: '🏰',
        description: 'Castles, monuments, ruins, and heritage sites',
        types: PLACE_TYPES.filter(type => type.category === 'cultural')
    },
    infrastructure: {
        name: 'Transportation & Infrastructure',
        icon: '🌉',
        description: 'Bridges, tunnels, ports, and built structures',
        types: PLACE_TYPES.filter(type => type.category === 'infrastructure')
    }
};

// Default selections (commonly used types)
export const DEFAULT_PLACE_TYPES = [
    'city', 'village', 'lake', 'mountain'
];

// Popular presets for quick selection
export const PLACE_TYPE_PRESETS = {
    settlements: {
        name: 'Common Settlements',
        icon: '🏘️',
        types: ['city', 'town', 'village', 'municipality', 'district']
    },
    nature: {
        name: 'Natural Features',
        icon: '🌲',
        types: ['lake', 'river', 'mountain', 'hill', 'forest', 'valley', 'island']
    },
    water: {
        name: 'Water Bodies',
        icon: '🌊',
        types: ['lake', 'river', 'stream', 'bay', 'waterfall', 'spring']
    },
    terrain: {
        name: 'Land Features',
        icon: '⛰️',
        types: ['mountain', 'hill', 'valley', 'canyon', 'plateau', 'ridge']
    },
    cultural: {
        name: 'Heritage Sites',
        icon: '🏰',
        types: ['castle', 'ruins', 'monument', 'archaeological_site']
    }
};

export default PlaceType;